import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { Button } from '@commons/utils/styledLibraryComponents';
import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { getTheme } from '@commons/utils/theme';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import NextOrderRow from './components/NextOrderRow';

import {
  CenteredContent,
  CloseIcon,
  Content,
  Header,
  Rows,
  StyledPopover,
  TriggerContainer,
} from './styledComponents';

const ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' };
const TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' };

const EMPTY_STATE_ICON_WIDTH = '105px';

const theme = getTheme();

const TRIGGER_BUTTON_STYLE = {
  width: '32px',
  height: '32px',
  minWidth: '32px',
  padding: '0px',
  borderRadius: theme.borders.radius.weaker,
};

const TRIGGER_ICON_STYLE = { width: '16px', height: '16px', margin: '0px' };

const NextOrders = (props) => {
  const { title, columns, loadOrders, emptyStateLabel, getOrderLink } = props;

  const anchorRef = useRef(null);

  const [isOpened, setIsOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const fetchOrders = async () => {
    setIsLoading(true);

    try {
      const nextOrders = await loadOrders();

      setOrders(nextOrders ?? []);
    } catch {
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const stopClickPropagation = (event) => {
    event.stopPropagation();
  };

  const handleOpen = () => {
    setIsOpened(true);

    fetchOrders();
  };

  const handleClose = () => {
    setIsOpened(false);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <CenteredContent>
          <DeepsightComponentLoader height={24} width={24} />
        </CenteredContent>
      );
    }

    if (!orders.length) {
      return (
        <EmptyState
          backgroundColor={theme.colors.greys.lightest}
          iconWidth={EMPTY_STATE_ICON_WIDTH}
          label={emptyStateLabel ?? i18next.t('GENERAL.NO_RESULT')}
        />
      );
    }

    const columnCount = getOrderLink ? columns.length + 1 : columns.length;

    return (
      <Rows columnCount={columnCount}>
        {orders.map((order) => (
          <NextOrderRow
            columns={columns}
            key={order.id}
            link={getOrderLink ? getOrderLink(order) : null}
            order={order}
            withLink={!!getOrderLink}
          />
        ))}
      </Rows>
    );
  };

  return (
    <>
      <TriggerContainer ref={anchorRef} onClick={stopClickPropagation}>
        <Button
          buttonCustomStyle={TRIGGER_BUTTON_STYLE}
          color={isOpened ? 'inpulse-default' : 'inpulse-outline'}
          handleClick={handleOpen}
          icon={`/images/inpulse/order-${isOpened ? 'white' : 'black'}-small.svg`}
          iconCustomStyle={TRIGGER_ICON_STYLE}
          label={''}
        />
      </TriggerContainer>
      <StyledPopover
        anchorEl={anchorRef.current}
        anchorOrigin={ANCHOR_ORIGIN}
        open={isOpened}
        transformOrigin={TRANSFORM_ORIGIN}
        disableScrollLock
        onClick={stopClickPropagation}
        onClose={handleClose}
      >
        <Header>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
            {title}
          </Text>
          <CloseIcon src={'/images/inpulse/close-black-small.svg'} onClick={handleClose} />
        </Header>
        <Content>{renderContent()}</Content>
      </StyledPopover>
    </>
  );
};

NextOrders.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      propertyKey: PropTypes.string.isRequired,
      render: PropTypes.func,
    }),
  ).isRequired,
  loadOrders: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  emptyStateLabel: PropTypes.string,
  getOrderLink: PropTypes.func,
};

NextOrders.defaultProps = {
  emptyStateLabel: null,
  getOrderLink: null,
};

export default NextOrders;
